<template>
  <div class="payments-page">

    <!-- PAGE HEADER -->
    <div class="payments-header">
      <div class="payments-header__left">
        <div class="payments-header__icon">
          <i class="fa-solid fa-credit-card"></i>
        </div>
        <div>
          <h2 class="payments-header__title">Transactions</h2>
          <p class="payments-header__sub" v-if="transactionsTotalCount !== null">
            {{ transactionsTotalCount }} total
          </p>
        </div>
      </div>
      <b-button variant="primary" class="btn-new-payment" @click="() => { showSidebar = true; resetNewPaymentForm(); }">
        <i class="fa-solid fa-plus mr-1"></i> New payment link
      </b-button>
    </div>

    <!-- ERROR BANNER -->
    <b-alert v-if="settingsError || transactionsError" variant="danger" show dismissible
      @dismissed="settingsError = null; transactionsError = null" class="mx-0 mb-4">
      {{ settingsError || transactionsError }}
    </b-alert>

    <!-- STATS DASHBOARD -->
    <div v-if="stats" class="stats-section">

      <!-- PERIOD TABS -->
      <div class="period-tabs">
        <button
          v-for="p in periodOptions"
          :key="p.value"
          class="period-tab"
          :class="{ 'period-tab--active': selectedPeriod === p.value }"
          @click="selectedPeriod = p.value"
        >
          {{ p.label }}
        </button>
        <div v-if="selectedPeriod === 'custom'" class="period-custom-range">
          <input type="date" class="filter-date-input" v-model="customDateFrom" :max="customDateTo || undefined" />
          <span class="filter-dates__sep">—</span>
          <input type="date" class="filter-date-input" v-model="customDateTo" :min="customDateFrom || undefined" />
        </div>
      </div>
      <b-alert v-if="dateRangeError" variant="warning" show dismissible @dismissed="dateRangeError = null" class="mx-0 mb-0 mt-2" style="font-size:0.85rem">
        {{ dateRangeError }}
      </b-alert>

      <!-- STAT CARDS -->
      <div v-if="currentPeriodData" class="stats-grid">
        <div class="stat-card stat-card--highlight">
          <div class="stat-card__label">Revenue</div>
          <div class="stat-card__value">
            <span v-if="currentPeriodData.revenue && currentPeriodData.revenue.length > 0">
              <span v-for="(r, i) in currentPeriodData.revenue" :key="r.currency">
                <span v-if="i > 0" class="stat-sep"> · </span>
                {{ formatStatAmount(r.totalAmount, r.precision) }} <span class="stat-currency">{{ r.currency }}</span>
              </span>
            </span>
            <span v-else class="stat-empty">—</span>
          </div>
          <div class="stat-card__sub"><i class="fa-solid fa-circle-check stat-icon--green"></i> Completed payments</div>
        </div>

        <div class="stat-card">
          <div class="stat-card__label">Total</div>
          <div class="stat-card__value">{{ currentPeriodData.totalCount }}</div>
          <div class="stat-card__sub">{{ currentPeriodData.byStatus.completed || 0 }} completed</div>
        </div>

        <div class="stat-card">
          <div class="stat-card__label">Pending</div>
          <div class="stat-card__value stat-card__value--pending">{{ currentPeriodData.byStatus.pending || 0 }}</div>
          <div class="stat-card__sub">Awaiting payment</div>
        </div>

        <div class="stat-card">
          <div class="stat-card__label">Failed</div>
          <div class="stat-card__value stat-card__value--failed">{{ currentPeriodData.byStatus.failed || 0 }}</div>
          <div class="stat-card__sub">
            Refunded: {{ currentPeriodData.byStatus.refunded || 0 }} · Voided: {{ currentPeriodData.byStatus.voided || 0 }}
          </div>
        </div>
      </div>

      <!-- STATUS BREAKDOWN BAR -->
      <div v-if="currentPeriodData && currentPeriodData.totalCount > 0" class="status-breakdown">
        <div
          v-for="seg in statusBreakdownSegments"
          :key="seg.status"
          class="status-breakdown__seg"
          :class="'status-breakdown__seg--' + seg.status"
          :style="{ width: seg.pct + '%' }"
          :title="seg.status + ': ' + seg.count"
        ></div>
      </div>

      <!-- TREND CHART + CURRENCY BREAKDOWN -->
      <div class="stats-bottom" :class="{ 'stats-bottom--full': !stats.currencyBreakdown || stats.currencyBreakdown.length === 0 }">
        <div class="trend-card">
          <div class="trend-card__title">Transaction Trend</div>
          <div class="trend-chart-wrap">
            <trend-line
              v-if="trendChartData"
              :chart-data="trendChartData"
              :options="trendChartOptions"
              :width="null"
              :height="null"
            />
            <div v-else class="trend-empty">No trend data for this period</div>
          </div>
        </div>

        <div v-if="stats.currencyBreakdown && stats.currencyBreakdown.length > 0" class="currency-card">
          <div class="currency-card__title">By Currency</div>
          <div v-for="c in stats.currencyBreakdown" :key="c.currency" class="currency-row">
            <div class="currency-row__name">{{ c.currency }}</div>
            <div class="currency-row__amount">{{ formatStatAmount(c.completedAmount, c.precision) }}</div>
            <div class="currency-row__badges">
              <span class="currency-count currency-count--completed" :title="'Completed: ' + (c.byStatus.completed || 0)">{{ c.byStatus.completed || 0 }}</span>
              <span class="currency-count currency-count--pending" :title="'Pending: ' + (c.byStatus.pending || 0)">{{ c.byStatus.pending || 0 }}</span>
              <span class="currency-count currency-count--failed" :title="'Failed: ' + (c.byStatus.failed || 0)">{{ c.byStatus.failed || 0 }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- MAIN CARD -->
    <div class="payments-card">

      <!-- STATUS FILTER -->
      <div class="filter-bar">
        <span class="filter-bar__label">Status</span>
        <div class="filter-chips">
          <button
            v-for="opt in statusOptions"
            :key="opt.value"
            class="filter-chip"
            :class="{ 'filter-chip--active': transactionStatus === opt.value }"
            @click="transactionStatus = opt.value"
          >
            {{ opt.text }}
          </button>
        </div>
      </div>

      <!-- LOADING -->
      <div v-if="settingsLoading || transactionsLoading" class="payments-loading">
        <b-spinner small /> <span>Loading transactions…</span>
      </div>

      <!-- EMPTY STATE -->
      <div v-else-if="!transactionsLoading && transactions.length === 0" class="payments-empty">
        <div class="payments-empty__icon"><i class="fa-solid fa-receipt"></i></div>
        <p class="payments-empty__text">No transactions found.</p>
        <b-button variant="primary" size="sm" @click="() => { showSidebar = true; resetNewPaymentForm(); }">
          Create your first payment link
        </b-button>
      </div>

      <!-- TABLE -->
      <div v-else class="payments-table-wrap">
        <table class="payments-table">
          <thead>
            <tr>
              <th>Transaction ID</th>
              <th>Provider</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Date</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="tx in transactions" :key="tx.transactionId" class="payments-table__row">
              <td>
                <span class="tx-id" :title="tx.transactionId">{{ tx.transactionId }}</span>
              </td>
              <td>
                <span class="provider-badge">{{ tx.provider }}</span>
              </td>
              <td>
                <span class="tx-amount">{{ tx.formattedAmount }} <span class="tx-currency">{{ tx.currency }}</span></span>
              </td>
              <td>
                <span class="status-badge" :class="'status-badge--' + tx.status">{{ tx.status }}</span>
              </td>
              <td class="tx-date">{{ tx.formattedDate }}</td>
              <td class="tx-actions">
                <div class="action-wrap">
                  <button
                    class="action-btn"
                    @click.stop="activeActionRow = activeActionRow === tx.transactionId ? null : tx.transactionId"
                  >
                    <i class="fas fa-ellipsis-v"></i>
                  </button>
                  <div v-if="activeActionRow === tx.transactionId" class="action-dropdown" @click.stop>
                    <button class="action-dropdown__item" @click="askConfirm('refund', tx); activeActionRow = null">
                      <i class="fa-solid fa-rotate-left"></i> Refund
                    </button>
                    <button class="action-dropdown__item" @click="askConfirm('void', tx); activeActionRow = null">
                      <i class="fa-solid fa-ban"></i> Void
                    </button>
                    <button class="action-dropdown__item" @click="askConfirm('deactivate', tx); activeActionRow = null">
                      <i class="fa-solid fa-circle-xmark"></i> Deactivate
                    </button>
                    <button v-if="tx.paymentLink && tx.status === 'pending'" class="action-dropdown__item" @click="copyLink(tx); activeActionRow = null">
                      <i class="fa-solid fa-copy"></i> Copy link
                    </button>
                    <div class="action-dropdown__divider"></div>
                    <button class="action-dropdown__item" @click="onViewMore(tx); activeActionRow = null">
                      <i class="fa-solid fa-eye"></i> View details
                    </button>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- PAGINATION -->
      <div v-if="!transactionsLoading && transactions.length > 0" class="payments-pagination">
        <PagePicker
          :currentPage="transactionsCurrentPage"
          :lastPage="transactionsTotalCount ? Math.floor(transactionsTotalCount / transactionsPageSize) : 0"
          :totalRowCount="transactionsTotalCount"
          @pagePicked="(p) => (transactionsCurrentPage = p)"
        />
      </div>
    </div>

    <!-- DETAIL DIALOG -->
    <main-dialog
      :show="showDetailSidebar"
      title="Transaction Details"
      max-width="600px"
      @close="showDetailSidebar = false"
    >
      <div v-if="detailTx">

        <!-- Header: amount + status -->
        <div class="detail-header">
          <div>
            <div class="detail-amount">{{ detailTx.formattedAmount }} <span class="detail-currency">{{ detailTx.currency }}</span></div>
            <div class="detail-header__meta">
              <span class="status-badge" :class="'status-badge--' + detailTx.status">{{ detailTx.status }}</span>
              <span class="detail-provider-badge">{{ detailTx.provider }}</span>
              <span v-if="detailTx.autoRefund" class="detail-tag">auto-refund</span>
            </div>
          </div>
          <div class="detail-dates">
            <div class="detail-dates__row"><span class="detail-dates__label">Created</span><span class="detail-dates__val">{{ formatDetailDate(detailTx.createdAt) }}</span></div>
            <div v-if="detailTx.updatedAt && detailTx.updatedAt !== detailTx.createdAt" class="detail-dates__row"><span class="detail-dates__label">Updated</span><span class="detail-dates__val">{{ formatDetailDate(detailTx.updatedAt) }}</span></div>
            <div v-if="detailTx.expiresAt" class="detail-dates__row"><span class="detail-dates__label">Expires</span><span class="detail-dates__val" :class="isExpired(detailTx.expiresAt) ? 'detail-dates__val--expired' : ''">{{ formatDetailDate(detailTx.expiresAt) }}</span></div>
          </div>
        </div>

        <!-- Section: Transaction info -->
        <div class="detail-section">
          <div class="detail-section__title">Transaction</div>
          <div class="detail-row">
            <span class="detail-row__label">Transaction ID</span>
            <div class="detail-row__copy-row">
              <span class="detail-row__value detail-row__value--mono">{{ detailTx.transactionId }}</span>
              <button class="detail-copy-btn" @click="copyToClipboard(detailTx.transactionId)"><i class="fa-solid fa-copy"></i></button>
            </div>
          </div>
          <div v-if="detailTx.orderId" class="detail-row">
            <span class="detail-row__label">Order ID</span>
            <span class="detail-row__value detail-row__value--mono">{{ detailTx.orderId }}</span>
          </div>
          <div v-if="detailTx.description" class="detail-row">
            <span class="detail-row__label">Description</span>
            <span class="detail-row__value">{{ detailTx.description }}</span>
          </div>
          <div v-if="detailTx.tokenizedPan" class="detail-row">
            <span class="detail-row__label">Card (tokenized)</span>
            <span class="detail-row__value detail-row__value--mono">{{ detailTx.tokenizedPan }}</span>
          </div>
        </div>

        <!-- Section: Payment link + Redirect URL -->
        <div v-if="detailTx.paymentLink || detailTx.redirectUrl" class="detail-section">
          <div class="detail-section__title">Links</div>
          <div v-if="detailTx.paymentLink" class="detail-row">
            <span class="detail-row__label">Payment link</span>
            <div class="sidebar-link-row mt-1">
              <input class="sidebar-link-input" :value="detailTx.paymentLink" readonly @focus="$event.target.select()"/>
              <button class="sidebar-copy-btn" @click="copyLink(detailTx)"><i class="fa-solid fa-copy"></i></button>
            </div>
          </div>
          <div v-if="detailTx.redirectUrl" class="detail-row">
            <span class="detail-row__label">Redirect URL</span>
            <div class="sidebar-link-row mt-1">
              <input class="sidebar-link-input" :value="detailTx.redirectUrl" readonly @focus="$event.target.select()"/>
              <button class="sidebar-copy-btn" @click="copyToClipboard(detailTx.redirectUrl)"><i class="fa-solid fa-copy"></i></button>
            </div>
          </div>
        </div>

        <!-- Section: Metadata / Items -->
        <div v-if="detailTx.metadata && detailMetaItems(detailTx).length" class="detail-section">
          <div class="detail-section__title">Items</div>
          <div class="detail-items-table">
            <div class="detail-items-head">
              <span>Item</span><span>Qty</span><span>Unit price</span><span>Total</span>
            </div>
            <div v-for="(item, idx) in detailMetaItems(detailTx)" :key="idx" class="detail-items-row">
              <span>{{ item.name }}</span>
              <span>{{ item.quantity != null ? item.quantity : '—' }}</span>
              <span>{{ item.pricePerUnit != null ? formatStatAmount(item.pricePerUnit, detailTx.precision) : '—' }}</span>
              <span>{{ formatStatAmount(item.price, detailTx.precision) }} {{ detailTx.currency }}</span>
            </div>
          </div>
        </div>

        <!-- Section: Status history (logs) -->
        <div v-if="detailTx.logs && detailTx.logs.length" class="detail-section">
          <div class="detail-section__title">Status history</div>
          <div class="detail-timeline">
            <div v-for="(log, idx) in detailTx.logs" :key="idx" class="detail-timeline__entry">
              <div class="detail-timeline__dot" :class="'detail-timeline__dot--' + log.status"></div>
              <div class="detail-timeline__body">
                <span class="detail-timeline__status">{{ log.status }}</span>
                <span class="detail-timeline__time">{{ formatDetailDate(log.timestamp) }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Actions -->
        <div class="detail-actions">
          <button class="detail-action-btn detail-action-btn--refund" @click="askConfirm('refund', detailTx)">
            <i class="fa-solid fa-rotate-left"></i> Refund
          </button>
          <button class="detail-action-btn detail-action-btn--void" @click="askConfirm('void', detailTx)">
            <i class="fa-solid fa-ban"></i> Void
          </button>
          <button class="detail-action-btn detail-action-btn--deactivate" @click="askConfirm('deactivate', detailTx)">
            <i class="fa-solid fa-circle-xmark"></i> Deactivate
          </button>
        </div>
      </div>
    </main-dialog>

    <!-- CONFIRM DIALOG -->
    <main-dialog
      :show="confirmDialog.show"
      :title="confirmDialogTitle"
      max-width="400px"
      @close="confirmDialog.show = false"
    >
      <div v-if="confirmDialog.tx">
        <p class="confirm-dialog__msg">{{ confirmDialogMessage }}</p>
        <div class="confirm-dialog__meta">
          <span class="detail-row__value detail-row__value--mono">{{ confirmDialog.tx.transactionId }}</span>
          <span class="detail-amount" style="font-size:1.2rem">{{ confirmDialog.tx.formattedAmount }} <span class="detail-currency">{{ confirmDialog.tx.currency }}</span></span>
        </div>
      </div>
      <template v-slot:footer>
        <div class="confirm-dialog__actions">
          <button class="confirm-dialog__cancel" :disabled="confirmDialog.loading" @click="confirmDialog.show = false">Cancel</button>
          <button class="confirm-dialog__confirm" :class="'confirm-dialog__confirm--' + confirmDialog.type" :disabled="confirmDialog.loading" @click="executeConfirm">
            <b-spinner v-if="confirmDialog.loading" small></b-spinner>
            <span v-else>{{ confirmDialogAction }}</span>
          </button>
        </div>
      </template>
    </main-dialog>

    <!-- SIDEBAR -->
    <b-sidebar title="New Payment Link" v-model="showSidebar" right shadow>
      <div class="sidebar-body">
        <div v-if="newTransaction">
          <div class="sidebar-success">
            <i class="fa-solid fa-circle-check sidebar-success__icon"></i>
            <p class="sidebar-success__title">Payment link created!</p>
          </div>
          <div class="sidebar-link-box">
            <label class="sidebar-label">Payment link</label>
            <div class="sidebar-link-row">
              <input class="sidebar-link-input" :value="newTransaction.paymentLink" readonly @focus="$event.target.select()"/>
              <button class="sidebar-copy-btn" @click="copyLink(newTransaction)">
                <i class="fa-solid fa-copy"></i>
              </button>
            </div>
          </div>
          <b-button variant="outline-secondary" class="w-100 mt-3" @click="resetNewPaymentForm">
            Create another
          </b-button>
        </div>

        <b-form v-else @submit.prevent="createPaymentLink">
          <div class="sidebar-field">
            <label class="sidebar-label">Provider</label>
            <b-form-select
              v-model="newPayment.provider"
              :options="paymentProviderOptions"
            />
          </div>

          <div class="sidebar-field">
            <label class="sidebar-label">Total Amount</label>
            <b-form-input
              v-model="newPayment.totalAmount"
              placeholder="e.g. 100, 99,40"
              :state="newPayment.totalAmount === '' ? null : amountState"
            />
            <b-form-invalid-feedback :state="amountState">
              Enter a valid amount (e.g. 10, 99,40)
            </b-form-invalid-feedback>
          </div>

          <div class="sidebar-field">
            <label class="sidebar-label">Description</label>
            <b-form-input
              v-model="newPayment.description"
              placeholder="Min 3 characters"
              :state="newPayment.description === '' ? null : descriptionState"
            />
            <b-form-invalid-feedback :state="descriptionState">
              Description must be at least 3 characters
            </b-form-invalid-feedback>
          </div>

          <div class="sidebar-field">
            <label class="sidebar-label">Currency</label>
            <b-form-select
              v-model="newPayment.currency"
              :options="paymentCurrencyOptions"
            />
          </div>

          <div class="sidebar-field">
            <label class="sidebar-label">Redirect URL <span class="sidebar-optional">(optional)</span></label>
            <b-form-input
              v-model="newPayment.redirectUrl"
              type="url"
              placeholder="https://example.com/thank-you"
            />
          </div>

          <b-alert v-if="newPaymentError" variant="danger" show dismissible @dismissed="newPaymentError = null">
            {{ newPaymentError }}
          </b-alert>

          <b-button type="submit" variant="primary" class="w-100" :disabled="!formValid || newPaymentLoading">
            <b-spinner v-if="newPaymentLoading" small label="Creating…" />
            <span v-else><i class="fa-solid fa-link mr-1"></i> Create link</span>
          </b-button>
        </b-form>
      </div>
    </b-sidebar>

  </div>
</template>

<script>
import MainDialog from "../../../MainDialog.vue";
import { Line, mixins } from "vue-chartjs";

var TrendLine = {
  extends: Line,
  mixins: [mixins.reactiveProp],
  props: {
    options: { type: Object, default: null },
  },
  mounted: function() {
    this.renderChart(this.chartData, this.options);
  },
};

import {
  BSidebar,
  BForm,
  BFormGroup,
  BFormInput,
  BInputGroup,
  BInputGroupAppend,
  BAlert,
  BSpinner,
  BFormSelect,
} from "bootstrap-vue";
import PagePicker from "./PagePicker.vue";

export default {
  name: "TransactionsList",
  components: {
    MainDialog,
    TrendLine,
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BInputGroup,
    BInputGroupAppend,
    BAlert,
    BSpinner,
    BFormSelect,
    PagePicker,
  },

  data() {
    return {
      // existing state
      settingsLoading: false,
      settingsError: null,
      transactionsLoading: false,
      transactionsError: null,
      transactions: [],
      transactionsTotalCount: null,
      transactionsCurrentPage: 0,
      transactionsPageSize: 10,

      transactionStatus: "",
      customDateFrom: "",
      customDateTo: "",
      dateRangeError: null,
      currentParams: {},
      statsLoading: false,
      statsError: null,
      stats: null,
      selectedPeriod: '30d',
      periodOptions: [
        { value: '7d',  label: '7 days'   },
        { value: '30d', label: '30 days'  },
        { value: '60d', label: '60 days'  },
        { value: '90d', label: '90 days'  },
        { value: 'custom', label: 'Custom' },
      ],
      statusOptions: [
        { value: '', text: 'Any' },
        { value: 'pending', text: 'Pending' },
        { value: 'completed', text: 'Completed' },
        { value: 'failed', text: 'Failed' },
        { value: 'refunded', text: 'Refunded' },
        { value: 'voided', text: 'Voided' },
        { value: 'deactivated', text: 'Deactivated' },
        { value: 'expired', text: 'Expired' },
      ],
      paymentProviderOptions: [
        { value: 'monri', text: 'Monri' },
        { value: 'stripe', text: 'Stripe' },
        { value: 'braintree', text: 'Braintree' },
      ],
      paymentCurrencyOptions: [
        { value: 'RSD', text: 'RSD' },
        { value: 'EUR', text: 'EUR' },
        { value: 'USD', text: 'USD' },
      ],

      // sidebar & form state
      showSidebar: false,
      showDetailSidebar: false,
      detailTx: null,
      activeActionRow: null,
      confirmDialog: {
        show: false,
        type: null,
        tx: null,
        loading: false,
      },
      newPayment: {
        provider: "monri",
        totalAmount: "",
        description: "",
        currency: "RSD",
        redirectUrl: "",
      },
      newTransaction: null,
      newPaymentLoading: false,
      newPaymentError: null,

      tableFields: [
        { key: "transactionId", label: "Transaction ID" },
        { key: "provider", label: "Provider" },
        { key: "formattedAmount", label: "Amount" },
        { key: "currency", label: "Currency" },
        { key: "status", label: "Status" },
        { key: "formattedDate", label: "Date" },
        { key: "actions", label: "", width: "80px" },
      ],
    };
  },

  computed: {
    projectUUID() {
      return this.$store.getters.getCurrentProject.uuid;
    },
    // validation states
    amountState() {
      const pattern = /^\d+(,\d{2})?$/;
      return (
        this.newPayment.totalAmount === "" ||
        pattern.test(this.newPayment.totalAmount)
      );
    },
    descriptionState() {
      return (
        this.newPayment.description === "" ||
        this.newPayment.description.length >= 3
      );
    },
    formValid() {
      return (
        !!this.newPayment.provider &&
        this.amountState &&
        this.newPayment.totalAmount !== "" &&
        this.descriptionState &&
        this.newPayment.description.length >= 3 &&
        /^[A-Z]{3}$/.test(this.newPayment.currency)
      );
    },
    activeDateRange() {
      if (this.selectedPeriod === 'custom') {
        return {
          dateFrom: this.customDateFrom || undefined,
          dateTo: this.customDateTo || undefined,
        };
      }
      var days = parseInt(this.selectedPeriod);
      if (!days) return {};
      var from = new Date();
      from.setDate(from.getDate() - days);
      var yyyy = from.getFullYear();
      var mm = from.getMonth() + 1;
      var dd = from.getDate();
      return {
        dateFrom: yyyy + '-' + (mm < 10 ? '0' + mm : '' + mm) + '-' + (dd < 10 ? '0' + dd : '' + dd),
      };
    },
    currentPeriodData() {
      if (!this.stats || !this.stats.periods) return null;
      if (this.selectedPeriod === 'custom') return this.stats.periods['90d'] || null;
      return this.stats.periods[this.selectedPeriod] || null;
    },
    confirmDialogTitle() {
      if (this.confirmDialog.type === 'refund') return 'Confirm Refund';
      if (this.confirmDialog.type === 'void') return 'Confirm Void';
      if (this.confirmDialog.type === 'deactivate') return 'Confirm Deactivation';
      return 'Confirm';
    },
    confirmDialogMessage() {
      if (this.confirmDialog.type === 'refund') return 'Are you sure you want to refund this transaction? The amount will be returned to the customer.';
      if (this.confirmDialog.type === 'void') return 'Are you sure you want to void this transaction? This action cannot be undone.';
      if (this.confirmDialog.type === 'deactivate') return 'Are you sure you want to deactivate this payment link? It will no longer be accessible.';
      return 'Are you sure?';
    },
    confirmDialogAction() {
      if (this.confirmDialog.type === 'refund') return 'Refund';
      if (this.confirmDialog.type === 'void') return 'Void';
      if (this.confirmDialog.type === 'deactivate') return 'Deactivate';
      return 'Confirm';
    },
    statusBreakdownSegments() {
      if (!this.currentPeriodData || this.currentPeriodData.totalCount === 0) return [];
      const order = ['completed', 'pending', 'refunded', 'failed', 'voided', 'deactivated', 'expired'];
      const segments = [];
      for (var i = 0; i < order.length; i++) {
        var status = order[i];
        var count = (this.currentPeriodData.byStatus && this.currentPeriodData.byStatus[status]) || 0;
        if (count > 0) {
          segments.push({
            status: status,
            count: count,
            pct: Math.round((count / this.currentPeriodData.totalCount) * 100),
          });
        }
      }
      return segments;
    },
    trendChartData() {
      if (!this.stats || !this.stats.dailyActivity) return null;
      var numDays;
      if (this.selectedPeriod === 'custom' && this.customDateFrom && this.customDateTo) {
        numDays = Math.min(90, Math.ceil((new Date(this.customDateTo) - new Date(this.customDateFrom)) / (1000 * 60 * 60 * 24)) + 1);
      } else {
        const dayMap = { '7d': 7, '30d': 30, '60d': 60, '90d': 90 };
        numDays = dayMap[this.selectedPeriod] || 30;
      }
      const startDate = (this.selectedPeriod === 'custom' && this.customDateFrom) ? new Date(this.customDateFrom) : null;
      const dates = [];
      var i, j;
      for (i = 0; i < numDays; i++) {
        var d = startDate ? new Date(startDate) : new Date();
        if (startDate) {
          d.setDate(d.getDate() + i);
        } else {
          d.setDate(d.getDate() - (numDays - 1 - i));
        }
        var yyyy = d.getFullYear();
        var mm = d.getMonth() + 1;
        var dd = d.getDate();
        dates.push(yyyy + '-' + (mm < 10 ? '0' + mm : '' + mm) + '-' + (dd < 10 ? '0' + dd : '' + dd));
      }
      const lookup = {};
      const activity = this.stats.dailyActivity;
      for (i = 0; i < activity.length; i++) {
        lookup[activity[i].date + '|' + activity[i].status] = activity[i].count;
      }
      const statuses = ['completed', 'pending', 'failed'];
      const colors = { completed: '#38ef7d', pending: '#fbbf08', failed: '#f87171' };
      const datasets = [];
      for (i = 0; i < statuses.length; i++) {
        var s = statuses[i];
        var data = [];
        for (j = 0; j < dates.length; j++) {
          data.push(lookup[dates[j] + '|' + s] || 0);
        }
        datasets.push({
          label: s.charAt(0).toUpperCase() + s.slice(1),
          data: data,
          borderColor: colors[s],
          backgroundColor: 'transparent',
          borderWidth: 2,
          pointRadius: 1,
          lineTension: 0.3,
          fill: false,
        });
      }
      return {
        labels: dates.map(function(date) { return date.slice(5).replace('-', '/'); }),
        datasets: datasets,
      };
    },
    trendChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: true,
          position: 'top',
          labels: { boxWidth: 12, fontColor: '#94a3b8', fontSize: 11 },
        },
        scales: {
          xAxes: [{
            ticks: { fontColor: '#64748b', fontSize: 10, maxTicksLimit: 12, maxRotation: 0 },
            gridLines: { color: 'rgba(255,255,255,0.04)', zeroLineColor: 'rgba(255,255,255,0.08)' },
          }],
          yAxes: [{
            ticks: { fontColor: '#64748b', fontSize: 10, beginAtZero: true, precision: 0 },
            gridLines: { color: 'rgba(255,255,255,0.04)', zeroLineColor: 'rgba(255,255,255,0.08)' },
          }],
        },
        tooltips: {
          mode: 'index',
          intersect: false,
          backgroundColor: 'rgba(15,23,42,0.95)',
          titleFontColor: '#e2e8f0',
          bodyFontColor: '#94a3b8',
          borderColor: 'rgba(255,255,255,0.1)',
          borderWidth: 1,
        },
      };
    },
  },

  created() {
    this.fetchPayments();
    this._closeDropdown = () => { this.activeActionRow = null; };
    document.addEventListener('click', this._closeDropdown);
  },

  mounted() {
    // ── Kortex AI tools ──────────────────────────────────────
    if (!window.kortex) return
    var self = this

    window.kortex.showHelper()
    window.kortex.registerContext(
      'Payments & Transactions',
      'BEFORE YOU DO ANYTHING ELSE: first search the internal knowledge base for relevant topics. ' +
      'The user is viewing the payment transactions dashboard. You can view payment statistics (revenue, counts, trends), ' +
      'list transactions, create new payment links, copy payment links, and check transaction details. ' +
      'Use get_payment_statistics for an overview, list_transactions to see recent activity, ' +
      'and create_payment_link to generate a new pay-by-link.',
      'fa-credit-card'
    )

    window.kortex.registerTool(
      {
        type: 'function',
        function: {
          name: 'get_payment_statistics',
          description: 'Returns payment statistics: revenue, total transaction count, and breakdown by status (completed, pending, failed, refunded, voided).',
          parameters: { type: 'object', properties: {} }
        }
      },
      function () {
        var s = self.stats
        if (!s) return { error: 'Statistics not loaded yet. Try refreshing.' }
        var periodData = self.currentPeriodData || {}
        return {
          total_count: periodData.totalCount || 0,
          revenue: (periodData.revenue || []).map(function (r) {
            return { amount: self.formatStatAmount(r.totalAmount, r.precision), currency: r.currency }
          }),
          by_status: {
            completed: periodData.byStatus ? periodData.byStatus.completed || 0 : 0,
            pending: periodData.byStatus ? periodData.byStatus.pending || 0 : 0,
            failed: periodData.byStatus ? periodData.byStatus.failed || 0 : 0,
            refunded: periodData.byStatus ? periodData.byStatus.refunded || 0 : 0,
            voided: periodData.byStatus ? periodData.byStatus.voided || 0 : 0
          },
          period: self.selectedPeriod
        }
      }
    )

    window.kortex.registerTool(
      {
        type: 'function',
        function: {
          name: 'list_transactions',
          description: 'Lists recent payment transactions with their status, amount, currency, and date.',
          parameters: {
            type: 'object',
            properties: {
              status: { type: 'string', description: 'Filter by status: pending, completed, failed, refunded, voided, deactivated, expired. Leave empty for all.' },
              page: { type: 'integer', description: 'Page number (1-based). Defaults to 1.' }
            }
          }
        }
      },
      function (args) {
        var list = self.transactions || []
        var statusFilter = (args && args.status || '').toLowerCase()
        if (statusFilter) {
          list = list.filter(function (tx) { return (tx.status || '').toLowerCase() === statusFilter })
        }
        var page = (args && args.page) || 1
        var pageSize = 10
        var start = (page - 1) * pageSize
        var pageItems = list.slice(start, start + pageSize)
        return {
          transactions: pageItems.map(function (tx) {
            return {
              id: tx.transactionId,
              amount: tx.formattedAmount,
              currency: tx.currency,
              status: tx.status,
              provider: tx.provider,
              date: tx.formattedDate || tx.createdAt,
              has_payment_link: !!tx.paymentLink
            }
          }),
          page: page,
          total: list.length
        }
      }
    )

    window.kortex.registerTool(
      {
        type: 'function',
        function: {
          name: 'create_payment_link',
          description: 'Creates a new payment link with the specified provider, amount, currency, and description.',
          parameters: {
            type: 'object',
            required: ['amount'],
            properties: {
              provider: { type: 'string', enum: ['monri', 'stripe', 'braintree'], description: 'Payment provider. Defaults to the provider selected in the form.' },
              amount: { type: 'string', description: 'The payment amount (e.g. "1500" or "1500,00").' },
              currency: { type: 'string', description: 'Three-letter currency code. Defaults to the selected form currency.' },
              description: { type: 'string', description: 'Payment description shown to the customer.' },
              redirect_url: { type: 'string', description: 'Optional URL to redirect after payment.' }
            }
          }
        }
      },
      async function (args) {
        if (!args.amount) return { error: 'Amount is required.' }
        var provider = ((args.provider || self.newPayment.provider || 'monri') + '').toLowerCase()
        var allowedProviders = self.paymentProviderOptions.map(function (opt) { return opt.value })
        if (allowedProviders.indexOf(provider) === -1) {
          return { error: 'Unsupported payment provider: ' + provider }
        }
        var currency = ((args.currency || self.newPayment.currency || 'RSD') + '').toUpperCase()
        var payload = { totalAmount: args.amount, description: args.description || '', currency: currency }
        if (args.redirect_url) payload.redirectUrl = args.redirect_url
        return self.$integrationApi.post(
          '/payments/' + provider + '/getPaymentLink',
          payload
        ).then(function (resp) {
          var tx = resp.data
          self.transactions.unshift(Object.assign({}, tx, { formattedDate: new Date(tx.createdAt).toLocaleString() }))
          return {
            payment_link: tx.paymentLink,
            transaction_id: tx.transactionId,
            amount: tx.formattedAmount,
            currency: tx.currency,
            status: tx.status,
            created_at: tx.createdAt
          }
        }).catch(function (err) {
          return { error: (err.response && err.response.data && err.response.data.message) || err.message || 'Failed to create payment link.' }
        })
      }
    )

    window.kortex.registerTool(
      {
        type: 'function',
        function: {
          name: 'find_transaction',
          description: 'Finds a specific transaction by its ID and returns full details.',
          parameters: {
            type: 'object',
            required: ['transaction_id'],
            properties: {
              transaction_id: { type: 'string', description: 'The transaction ID to look up.' }
            }
          }
        }
      },
      function (args) {
        var id = (args.transaction_id || '').trim()
        var tx = self.transactions.find(function (t) {
          return t.transactionId === id || (t.transactionId || '').indexOf(id) !== -1
        })
        if (!tx) return { error: 'Transaction not found: ' + id }
        return {
          id: tx.transactionId,
          amount: tx.formattedAmount,
          currency: tx.currency,
          status: tx.status,
          provider: tx.provider,
          date: tx.createdAt,
          payment_link: tx.paymentLink || null,
          description: tx.description || '',
          metadata: tx.metadata || null
        }
      }
    )
  },

  beforeDestroy() {
    document.removeEventListener('click', this._closeDropdown);
    if (!window.kortex) return
    window.kortex.unregisterContext()
    window.kortex.hideHelper()
    window.kortex.unregisterTool('get_payment_statistics')
    window.kortex.unregisterTool('list_transactions')
    window.kortex.unregisterTool('create_payment_link')
    window.kortex.unregisterTool('find_transaction')
  },

  methods: {
    async fetchPayments() {
      this.settingsLoading = true;
      this.settingsError = null;

      try {
        await this.fetchTransactions();
        this.fetchStatistics();
      } catch (err) {
        console.error("Error fetching payments:", err);
        this.settingsError =
          err.response.data.message ||
          err.message ||
          "Failed to load payments.";
      } finally {
        this.settingsLoading = false;
      }
    },

    validateDateRange() {
      this.dateRangeError = null;
      var range = this.activeDateRange;
      if (range.dateFrom && range.dateTo) {
        var diff = (new Date(range.dateTo) - new Date(range.dateFrom)) / (1000 * 60 * 60 * 24);
        if (diff > 90) {
          this.dateRangeError = "Date range cannot exceed 90 days.";
          return false;
        }
        if (diff < 0) {
          this.dateRangeError = "Start date must be before end date.";
          return false;
        }
      }
      return true;
    },

    async fetchTransactions() {
      if (!this.validateDateRange()) return;
      this.transactionsLoading = true;
      this.transactionsError = null;
      var range = this.activeDateRange;
      const params = {
        status: this.transactionStatus || undefined,
        limit: this.transactionsPageSize,
        page: this.transactionsCurrentPage,
        dateFrom: range.dateFrom,
        dateTo: range.dateTo,
      };
      this.currentParams = JSON.stringify(params);

      try {
        const resp = await this.$integrationApi.get(
          `/payments/list`,
          {
            params,
          }
        );

        if (this.currentParams === JSON.stringify(params)) {
          const rawList = resp.data.items || resp.data.transactions || [];
          const totalCount =
            resp.data.total !== undefined ? resp.data.total : resp.data.totalCount;
          this.transactionsTotalCount = totalCount;
          this.transactions = rawList.map((tx) => ({
            ...tx,
            formattedDate: new Date(tx.createdAt).toLocaleString(),
          }));
        }
      } catch (err) {
        console.error("Error fetching transactions:", err);
        this.transactionsError =
          err.response.data.message ||
          err.message ||
          "Failed to load transactions.";
      } finally {
        this.transactionsLoading = false;
      }
    },

    resetNewPaymentForm() {
      this.newPayment = {
        provider: "monri",
        totalAmount: "",
        description: "",
        currency: "RSD",
        redirectUrl: "",
      };
      this.newTransaction = null;
      this.newPaymentError = null;
      this.newPaymentLoading = false;
    },

    async fetchStatistics() {
      this.statsLoading = true;
      this.statsError = null;
      try {
        var range = this.activeDateRange;
        const params = {};
        if (range.dateFrom) params.dateFrom = range.dateFrom;
        if (range.dateTo) params.dateTo = range.dateTo;
        const resp = await this.$integrationApi.get(
          `/payments/statistics`,
          { params }
        );
        this.stats = resp.data;
      } catch (err) {
        console.error("Error fetching statistics:", err);
        this.statsError = (err.response && err.response.data && err.response.data.message) || err.message || "Failed to load statistics.";
      } finally {
        this.statsLoading = false;
      }
    },

    formatStatAmount(amount, precision) {
      var divisor = Math.pow(10, precision || 2);
      return (amount / divisor).toFixed(precision || 2);
    },

    formatDetailDate(val) {
      if (!val) return '—';
      var d = new Date(val);
      if (isNaN(d.getTime())) return val;
      return d.toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    },

    isExpired(val) {
      if (!val) return false;
      return new Date(val) < new Date();
    },

    detailMetaItems(tx) {
      if (!tx || !tx.metadata || !tx.metadata.items) return [];
      return tx.metadata.items;
    },

    copyToClipboard(text) {
      if (!text) return;
      if (navigator.clipboard) {
        navigator.clipboard.writeText(text);
      } else {
        var el = document.createElement('textarea');
        el.value = text;
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
      }
    },

    async createPaymentLink() {
      this.newPaymentError = null;
      this.newPaymentLoading = true;

      try {
        const payload = {
          totalAmount: this.newPayment.totalAmount,
          description: this.newPayment.description,
          currency: this.newPayment.currency,
        };
        if (this.newPayment.redirectUrl) {
          payload.redirectUrl = this.newPayment.redirectUrl;
        }

        const resp = await this.$integrationApi.post(
          `/payments/${this.newPayment.provider}/getPaymentLink`,
          payload
        );
        const newTx = resp.data;

        this.newTransaction = newTx;
        this.transactions.unshift({
          ...this.newTransaction,
          formattedDate: new Date(
            this.newTransaction.createdAt
          ).toLocaleString(),
        });
      } catch (err) {
        console.error("Error creating payment link:", err);
        this.newPaymentError =
          err.response.data.message ||
          err.message ||
          "Failed to create payment link.";
      } finally {
        this.newPaymentLoading = false;
      }
    },

    copyLink(tx) {
      navigator.clipboard.writeText(tx.paymentLink).then(() => {
        this.$bvToast.toast("Copied!", {
          variant: "success",
          title: "Link copied to clipboard.",
        });
      });
    },

    askConfirm(type, tx) {
      this.confirmDialog.type = type;
      this.confirmDialog.tx = tx;
      this.confirmDialog.loading = false;
      this.confirmDialog.show = true;
    },

    async executeConfirm() {
      var type = this.confirmDialog.type;
      var tx = this.confirmDialog.tx;
      if (!type || !tx) return;
      this.confirmDialog.loading = true;
      try {
        if (type === 'refund') { await this.onRefund(tx); }
        else if (type === 'void') { await this.onVoid(tx); }
        else if (type === 'deactivate') { await this.onDeactivate(tx); }
        this.confirmDialog.show = false;
        this.showDetailSidebar = false;
      } finally {
        this.confirmDialog.loading = false;
      }
    },

    async onRefund(tx) {
      try {
        const resp = await this.$integrationApi.post(
          `/payments/${tx.transactionId}/refund`,
          {}
        );
        const newTx = resp.data;
        const index = this.transactions.findIndex(
          (t) => t.transactionId === newTx.transactionId
        );
        if (index >= 0) {
          newTx.formattedDate = new Date(newTx.createdAt).toLocaleString();
          this.$set(this.transactions, index, newTx);
        }
        this.$bvToast.toast("Refunded!", {
          variant: "success",
          title: "Transaction successfully refunded",
        });
      } catch (err) {
        console.error("Error refunding transaction:", err);
        this.transactionsError =
          err.response.data.message ||
          err.message ||
          "Failed to refund transaction.";
      }
    },

    async onVoid(tx) {
      try {
        const resp = await this.$integrationApi.post(
          `/payments/${tx.transactionId}/void`,
          {}
        );
        const newTx = resp.data;
        const index = this.transactions.findIndex(
          (t) => t.transactionId === newTx.transactionId
        );
        if (index >= 0) {
          newTx.formattedDate = new Date(newTx.createdAt).toLocaleString();
          this.$set(this.transactions, index, newTx);
        }
        this.$bvToast.toast("Voided!", {
          variant: "success",
          title: "Transaction successfully voided",
        });
      } catch (err) {
        console.error("Error voiding transaction:", err);
        this.transactionsError =
          err.response.data.message ||
          err.message ||
          "Failed to void transaction.";
      }
    },

    async onDeactivate(tx) {
      try {
        const resp = await this.$integrationApi.post(
          `/payments/${tx.transactionId}/deactivate`,
          {}
        );
        const newTx = resp.data;
        const index = this.transactions.findIndex(
          (t) => t.transactionId === newTx.transactionId
        );
        if (index >= 0) {
          newTx.formattedDate = new Date(newTx.createdAt).toLocaleString();
          this.$set(this.transactions, index, newTx);
        }
        this.$bvToast.toast("Deactivated!", {
          variant: "success",
          title: "Payment link successfully deactivated",
        });
      } catch (err) {
        console.error("Error deactivating payment link:", err);
        this.transactionsError =
          err.response.data.message ||
          err.message ||
          "Failed to deactive payment link.";
      }
    },

    onViewMore(tx) {
      this.detailTx = tx;
      this.showDetailSidebar = true;
    },
  },
  watch: {
    transactionsCurrentPage() {
      this.fetchTransactions();
    },
    transactionStatus() {
      this.fetchTransactions();
    },
    selectedPeriod() {
      this.transactionsCurrentPage = 0;
      this.fetchTransactions();
      this.fetchStatistics();
    },
    customDateFrom() {
      if (this.selectedPeriod === 'custom') {
        this.transactionsCurrentPage = 0;
        this.fetchTransactions();
        this.fetchStatistics();
      }
    },
    customDateTo() {
      if (this.selectedPeriod === 'custom') {
        this.transactionsCurrentPage = 0;
        this.fetchTransactions();
        this.fetchStatistics();
      }
    },
  },
};
</script>

<style scoped>
.payments-page {
  padding: 8px 0;
}

/* HEADER */
.payments-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
}
.payments-header__left {
  display: flex;
  align-items: center;
  gap: 14px;
}
.payments-header__icon {
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: linear-gradient(135deg, #11998e, #38ef7d);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  color: #fff;
  flex-shrink: 0;
}
.payments-header__title {
  font-size: 1.4rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
}
.payments-header__sub {
  font-size: 0.8rem;
  color: var(--text-muted);
  margin: 0;
}
.btn-new-payment {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
  border-radius: 10px;
  padding: 8px 18px;
}

/* CARD */
.payments-card {
  background: var(--bg-card);
  border: 1px solid var(--border-default);
  border-radius: 14px;
  overflow: hidden;
}

/* FILTER BAR */
.filter-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid var(--border-default);
}
.filter-bar__label {
  font-size: 0.78rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-muted);
  white-space: nowrap;
}
.filter-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.filter-chip {
  padding: 4px 12px;
  border-radius: 20px;
  border: 1px solid var(--border-default);
  background: transparent;
  color: var(--text-muted);
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.15s;
  outline: none;
}
.filter-chip:hover {
  border-color: #11998e;
  color: #11998e;
}
.filter-chip--active {
  background: linear-gradient(135deg, #11998e, #38ef7d);
  border-color: transparent;
  color: #fff;
  font-weight: 600;
}
.period-custom-range {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: 8px;
}
.filter-dates__sep {
  color: var(--text-muted);
  font-size: 0.8rem;
}
.filter-date-input {
  background: rgba(255,255,255,0.05);
  border: 1px solid var(--border-default);
  border-radius: 8px;
  padding: 4px 10px;
  color: var(--text-primary);
  font-size: 0.8rem;
  outline: none;
  transition: border-color 0.15s;
}
.filter-date-input:focus {
  border-color: #11998e;
}

/* LOADING */
.payments-loading {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 40px 20px;
  color: var(--text-muted);
  justify-content: center;
}

/* EMPTY STATE */
.payments-empty {
  text-align: center;
  padding: 60px 20px;
}
.payments-empty__icon {
  font-size: 2.5rem;
  color: var(--text-muted);
  margin-bottom: 12px;
  opacity: 0.4;
}
.payments-empty__text {
  color: var(--text-muted);
  margin-bottom: 16px;
}

/* TABLE */
.payments-table-wrap {
  overflow-x: auto;
}
.payments-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}
.payments-table thead tr {
  border-bottom: 1px solid var(--border-default);
}
.payments-table th {
  padding: 12px 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-muted);
  white-space: nowrap;
}
.payments-table__row {
  border-bottom: 1px solid var(--border-default);
  transition: background 0.12s;
}
.payments-table__row:last-child {
  border-bottom: none;
}
.payments-table__row:hover {
  background: rgba(255, 255, 255, 0.03);
}
.payments-table td {
  padding: 14px 20px;
  color: var(--text-primary);
  vertical-align: middle;
}
.tx-id {
  font-family: monospace;
  font-size: 0.8rem;
  color: var(--text-muted);
  display: block;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.provider-badge {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 6px;
  background: rgba(255,255,255,0.06);
  font-size: 0.78rem;
  font-weight: 600;
  color: var(--text-primary);
  text-transform: capitalize;
}
.tx-amount {
  font-weight: 600;
  color: var(--text-primary);
}
.tx-currency {
  font-size: 0.75rem;
  color: var(--text-muted);
  margin-left: 3px;
}
.tx-date {
  color: var(--text-muted);
  font-size: 0.82rem;
  white-space: nowrap;
}
.tx-actions {
  text-align: right;
  width: 48px;
}
.action-btn {
  color: var(--text-muted) !important;
  padding: 4px 8px;
  border-radius: 6px;
}
.action-btn:hover {
  background: rgba(255,255,255,0.06);
  color: var(--text-primary) !important;
}

/* STATUS BADGES */
.status-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: capitalize;
}
.status-badge--completed  { background: rgba(17,153,142,0.18); color: #38ef7d; }
.status-badge--pending    { background: rgba(251,189,8,0.15);  color: #fbbf08; }
.status-badge--failed     { background: rgba(239,68,68,0.15);  color: #f87171; }
.status-badge--expired    { background: rgba(148,163,184,0.1); color: #94a3b8; }
.status-badge--refunded   { background: rgba(99,102,241,0.15); color: #a5b4fc; }
.status-badge--voided     { background: rgba(148,163,184,0.1); color: #94a3b8; }
.status-badge--deactivated{ background: rgba(148,163,184,0.1); color: #94a3b8; }

/* ACTION WRAP / CUSTOM DROPDOWN */
.action-wrap {
  position: relative;
  display: inline-block;
}
.action-btn {
  background: none;
  border: none;
  color: var(--text-muted);
  padding: 4px 8px;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.12s, color 0.12s;
  line-height: 1;
}
.action-btn:hover {
  background: rgba(255,255,255,0.06);
  color: var(--text-primary);
}
.action-dropdown {
  position: absolute;
  right: 0;
  top: calc(100% + 4px);
  z-index: 1000;
  background: #1e2433;
  border: 1px solid rgba(255,255,255,0.1);
  border-radius: 10px;
  padding: 6px;
  min-width: 170px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.45);
}
.action-dropdown__item {
  display: flex;
  align-items: center;
  gap: 9px;
  width: 100%;
  padding: 8px 10px;
  background: none;
  border: none;
  color: #c8d3e6;
  font-size: 0.85rem;
  cursor: pointer;
  border-radius: 7px;
  text-align: left;
  transition: background 0.12s, color 0.12s;
}
.action-dropdown__item:hover {
  background: rgba(255,255,255,0.07);
  color: #fff;
}
.action-dropdown__item i {
  width: 14px;
  text-align: center;
  color: #64748b;
}
.action-dropdown__item:hover i {
  color: #94a3b8;
}
.action-dropdown__divider {
  height: 1px;
  background: rgba(255,255,255,0.07);
  margin: 4px 6px;
}

/* DETAIL DIALOG */
.detail-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--border-default);
  margin-bottom: 0;
}
.detail-header__meta {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  flex-wrap: wrap;
}
.detail-amount {
  font-size: 2rem;
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1.1;
}
.detail-currency {
  font-size: 1rem;
  color: var(--text-muted);
  margin-left: 4px;
}
.detail-provider-badge {
  display: inline-block;
  padding: 2px 9px;
  border-radius: 6px;
  background: rgba(255,255,255,0.07);
  border: 1px solid var(--border-default);
  color: var(--text-muted);
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
.detail-tag {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 6px;
  background: rgba(99,102,241,0.14);
  border: 1px solid rgba(99,102,241,0.3);
  color: #a5b4fc;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.04em;
}
.detail-dates {
  text-align: right;
  flex-shrink: 0;
}
.detail-dates__row {
  display: flex;
  gap: 8px;
  align-items: center;
  justify-content: flex-end;
  margin-bottom: 3px;
}
.detail-dates__label {
  font-size: 0.7rem;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.05em;
}
.detail-dates__val {
  font-size: 0.78rem;
  color: var(--text-primary);
}
.detail-dates__val--expired {
  color: #f87171;
}
.detail-section {
  padding: 14px 0;
  border-bottom: 1px solid var(--border-default);
}
.detail-section__title {
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-muted);
  margin-bottom: 10px;
}
.detail-row {
  margin-bottom: 12px;
}
.detail-row:last-child { margin-bottom: 0; }
.detail-row__label {
  display: block;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--text-muted);
  margin-bottom: 3px;
}
.detail-row__value {
  font-size: 0.88rem;
  color: var(--text-primary);
  word-break: break-all;
}
.detail-row__value--mono {
  font-family: monospace;
  font-size: 0.82rem;
  color: #94a3b8;
}
.detail-row__copy-row {
  display: flex;
  align-items: center;
  gap: 8px;
}
.detail-copy-btn {
  background: none;
  border: 1px solid var(--border-default);
  color: var(--text-muted);
  border-radius: 6px;
  padding: 2px 7px;
  font-size: 0.78rem;
  cursor: pointer;
  transition: color 0.12s, border-color 0.12s;
  flex-shrink: 0;
}
.detail-copy-btn:hover { color: var(--text-primary); border-color: rgba(255,255,255,0.25); }
/* Items table */
.detail-items-table {
  width: 100%;
  font-size: 0.82rem;
}
.detail-items-head {
  display: grid;
  grid-template-columns: 1fr 60px 100px 110px;
  gap: 8px;
  color: var(--text-muted);
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding-bottom: 6px;
  border-bottom: 1px solid var(--border-default);
  margin-bottom: 4px;
}
.detail-items-row {
  display: grid;
  grid-template-columns: 1fr 60px 100px 110px;
  gap: 8px;
  color: var(--text-primary);
  padding: 5px 0;
  border-bottom: 1px solid rgba(255,255,255,0.04);
}
.detail-items-row:last-child { border-bottom: none; }
/* Timeline */
.detail-timeline {
  display: flex;
  flex-direction: column;
  gap: 0;
}
.detail-timeline__entry {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 6px 0;
  position: relative;
}
.detail-timeline__entry::before {
  content: '';
  position: absolute;
  left: 7px;
  top: 22px;
  bottom: -6px;
  width: 1px;
  background: var(--border-default);
}
.detail-timeline__entry:last-child::before { display: none; }
.detail-timeline__dot {
  width: 15px;
  height: 15px;
  border-radius: 50%;
  border: 2px solid var(--border-default);
  background: #1e2433;
  flex-shrink: 0;
  margin-top: 2px;
}
.detail-timeline__dot--completed  { border-color: #34d399; background: rgba(52,211,153,0.15); }
.detail-timeline__dot--pending     { border-color: #fbbf24; background: rgba(251,191,36,0.15); }
.detail-timeline__dot--failed      { border-color: #f87171; background: rgba(248,113,113,0.15); }
.detail-timeline__dot--refunded    { border-color: #a78bfa; background: rgba(167,139,250,0.15); }
.detail-timeline__dot--voided      { border-color: #94a3b8; background: rgba(148,163,184,0.1); }
.detail-timeline__dot--deactivated { border-color: #64748b; background: rgba(100,116,139,0.1); }
.detail-timeline__dot--expired     { border-color: #64748b; background: rgba(100,116,139,0.1); }
.detail-timeline__body {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  flex-wrap: wrap;
  gap: 4px;
}
.detail-timeline__status {
  font-size: 0.82rem;
  color: var(--text-primary);
  font-weight: 500;
}
.detail-timeline__time {
  font-size: 0.75rem;
  color: var(--text-muted);
}
/* Actions */
.detail-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  padding-top: 16px;
}
.detail-action-btn {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 8px 14px;
  border-radius: 9px;
  border: 1px solid var(--border-default);
  background: transparent;
  color: var(--text-primary);
  font-size: 0.83rem;
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s, color 0.12s;
}
.detail-action-btn:hover { background: rgba(255,255,255,0.05); }
.detail-action-btn--refund:hover     { border-color: #a5b4fc; color: #a5b4fc; }
.detail-action-btn--void:hover       { border-color: #94a3b8; color: #94a3b8; }
.detail-action-btn--deactivate:hover { border-color: #f87171; color: #f87171; }

/* CONFIRM DIALOG */
.confirm-dialog__msg {
  font-size: 0.9rem;
  color: var(--text-primary);
  margin-bottom: 14px;
  line-height: 1.55;
}
.confirm-dialog__meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 14px;
  background: rgba(255,255,255,0.04);
  border: 1px solid var(--border-default);
  border-radius: 10px;
}
.confirm-dialog__actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}
.confirm-dialog__cancel {
  padding: 8px 18px;
  border-radius: 9px;
  border: 1px solid var(--border-default);
  background: transparent;
  color: var(--text-muted);
  font-size: 0.85rem;
  cursor: pointer;
  transition: color 0.12s, border-color 0.12s;
}
.confirm-dialog__cancel:hover:not(:disabled) { color: var(--text-primary); border-color: rgba(255,255,255,0.25); }
.confirm-dialog__cancel:disabled { opacity: 0.5; cursor: not-allowed; }
.confirm-dialog__confirm {
  padding: 8px 20px;
  border-radius: 9px;
  border: 1px solid transparent;
  background: rgba(248,113,113,0.15);
  border-color: rgba(248,113,113,0.4);
  color: #f87171;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s;
  display: flex;
  align-items: center;
  gap: 8px;
}
.confirm-dialog__confirm:hover:not(:disabled) { background: rgba(248,113,113,0.25); }
.confirm-dialog__confirm:disabled { opacity: 0.6; cursor: not-allowed; }
.confirm-dialog__confirm--refund  { background: rgba(165,180,252,0.15); border-color: rgba(165,180,252,0.4); color: #a5b4fc; }
.confirm-dialog__confirm--refund:hover:not(:disabled)  { background: rgba(165,180,252,0.25); }
.confirm-dialog__confirm--void    { background: rgba(148,163,184,0.12); border-color: rgba(148,163,184,0.35); color: #94a3b8; }
.confirm-dialog__confirm--void:hover:not(:disabled)    { background: rgba(148,163,184,0.22); }

/* PAGINATION */
.payments-pagination {
  display: flex;
  justify-content: center;
  padding: 16px 20px;
  border-top: 1px solid var(--border-default);
}

/* SIDEBAR */
.sidebar-body {
  padding: 20px;
}
.sidebar-field {
  margin-bottom: 18px;
}
.sidebar-label {
  display: block;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--text-muted);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 6px;
}
.sidebar-optional {
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0;
}
.sidebar-success {
  text-align: center;
  padding: 24px 0 16px;
}
.sidebar-success__icon {
  font-size: 2.5rem;
  color: #38ef7d;
  display: block;
  margin-bottom: 10px;
}
.sidebar-success__title {
  font-weight: 600;
  font-size: 1rem;
  color: var(--text-primary);
}
.sidebar-link-box {
  margin-top: 16px;
}
.sidebar-link-row {
  display: flex;
  gap: 8px;
}
.sidebar-link-input {
  flex: 1;
  background: rgba(255,255,255,0.05);
  border: 1px solid var(--border-default);
  border-radius: 8px;
  padding: 8px 12px;
  color: var(--text-primary);
  font-size: 0.82rem;
  font-family: monospace;
  outline: none;
}
.sidebar-copy-btn {
  padding: 8px 14px;
  background: rgba(255,255,255,0.07);
  border: 1px solid var(--border-default);
  border-radius: 8px;
  color: var(--text-primary);
  cursor: pointer;
  transition: background 0.12s;
}
.sidebar-copy-btn:hover {
  background: rgba(255,255,255,0.12);
}

/* STATS GRID */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
  margin-bottom: 16px;
}
.stat-card {
  background: var(--bg-card);
  border: 1px solid var(--border-default);
  border-radius: 14px;
  padding: 18px 20px;
}
.stat-card--highlight {
  border-color: rgba(17,153,142,0.35);
  background: rgba(17,153,142,0.06);
}
.stat-card__label {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-muted);
  margin-bottom: 8px;
}
.stat-card__value {
  font-size: 1.6rem;
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1.1;
  margin-bottom: 6px;
}
.stat-card__value--pending  { color: #fbbf08; }
.stat-card__value--failed   { color: #f87171; }
.stat-card__sub {
  font-size: 0.78rem;
  color: var(--text-muted);
}
.stat-icon--green { color: #38ef7d; margin-right: 4px; }
.stat-currency { font-size: 0.85rem; color: var(--text-muted); }
.stat-sep { color: var(--text-muted); }
.stat-empty { color: var(--text-muted); font-size: 1.2rem; }

/* STATUS BREAKDOWN BAR */
.status-breakdown {
  display: flex;
  height: 6px;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 16px;
  gap: 2px;
}
.status-breakdown__seg { height: 100%; border-radius: 3px; }
.status-breakdown__seg--completed   { background: #38ef7d; }
.status-breakdown__seg--pending     { background: #fbbf08; }
.status-breakdown__seg--refunded    { background: #a5b4fc; }
.status-breakdown__seg--failed      { background: #f87171; }
.status-breakdown__seg--voided      { background: #94a3b8; }
.status-breakdown__seg--deactivated { background: #64748b; }
.status-breakdown__seg--expired     { background: #475569; }

/* STATS SECTION */
.stats-section {
  margin-bottom: 24px;
}

/* PERIOD TABS */
.period-tabs {
  display: flex;
  gap: 4px;
  margin-bottom: 16px;
}
.period-tab {
  padding: 5px 14px;
  border-radius: 8px;
  border: 1px solid var(--border-default);
  background: transparent;
  color: var(--text-muted);
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  outline: none;
}
.period-tab:hover {
  border-color: #11998e;
  color: #38ef7d;
}
.period-tab--active {
  background: linear-gradient(135deg, #11998e, #38ef7d);
  border-color: transparent;
  color: #fff;
  font-weight: 600;
}

/* TREND + CURRENCY ROW */
.stats-bottom {
  display: grid;
  grid-template-columns: 1fr 260px;
  gap: 12px;
  margin-top: 12px;
}
.stats-bottom--full {
  grid-template-columns: 1fr;
}
.trend-card {
  background: var(--bg-card);
  border: 1px solid var(--border-default);
  border-radius: 14px;
  padding: 16px 20px;
}
.trend-card__title {
  font-size: 0.78rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-muted);
  margin-bottom: 12px;
}
.trend-chart-wrap {
  height: 160px;
  position: relative;
  overflow: hidden;
}
.trend-chart-wrap canvas {
  max-width: 100% !important;
  max-height: 100% !important;
}
.trend-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 140px;
  color: var(--text-muted);
  font-size: 0.85rem;
}

/* CURRENCY CARD */
.currency-card {
  background: var(--bg-card);
  border: 1px solid var(--border-default);
  border-radius: 14px;
  padding: 16px 20px;
  overflow-y: auto;
}
.currency-card__title {
  font-size: 0.78rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--text-muted);
  margin-bottom: 12px;
}
.currency-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 0;
  border-bottom: 1px solid var(--border-default);
}
.currency-row:last-child {
  border-bottom: none;
}
.currency-row__name {
  font-weight: 700;
  font-size: 0.85rem;
  color: var(--text-primary);
  min-width: 42px;
}
.currency-row__amount {
  font-size: 0.88rem;
  font-weight: 600;
  color: #38ef7d;
  flex: 1;
}
.currency-row__badges {
  display: flex;
  gap: 4px;
}
.currency-count {
  display: inline-block;
  padding: 1px 7px;
  border-radius: 10px;
  font-size: 0.72rem;
  font-weight: 600;
  cursor: default;
}
.currency-count--completed { background: rgba(17,153,142,0.18); color: #38ef7d; }
.currency-count--pending   { background: rgba(251,189,8,0.15);  color: #fbbf08; }
.currency-count--failed    { background: rgba(239,68,68,0.15);  color: #f87171; }
</style>
